import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import Button from './Button';
import { PageLoading } from './Skeleton';
import { useConferenceContext } from '../../contexts/ConferenceContext';
import UniversalNavbar from './UniversalNavbar';

/**
 * Gate a conference screen on the roles the person holds *in that conference*.
 *
 * The management screens had no role check at all. The conference dashboard came
 * closest: it fetched the user's roles and then only `console.log`ed them — the
 * result was never used for anything. So a reviewer who typed the URL (or kept
 * an old link) got the full chair interface: conference settings, the team
 * editor, every submission, the email templates.
 *
 * Row-level security in the database still governs what can actually be *read
 * and written*, so this is not the last line of defence — but rendering a
 * settings form to someone who can't save it is its own bug. It looks like a
 * permission they have until the moment it fails.
 *
 * Three states, in this order:
 *   loading  → say nothing yet. Flashing "no access" before the roles arrive
 *              accuses people of something they haven't done.
 *   denied   → say plainly what's missing and give a way out, not a blank page.
 *   allowed  → render.
 *
 *   <RequireConferenceRole manage>
 *     …chair-only screen…
 *   </RequireConferenceRole>
 */
const RequireConferenceRole = ({
  children,
  manage = false,   // chair or organiser
  reviewer = false,
  author = false,
}) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { loading, conferenceId, canManage, isReviewer, isAuthor } = useConferenceContext();

  // No conference in scope: the page's own missing-id handling owns this case.
  if (!conferenceId) return children;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <UniversalNavbar />
        <PageLoading label={t('access.checking', 'Checking your access…')} />
      </div>
    );
  }

  const wanted = [manage && canManage, reviewer && isReviewer, author && isAuthor];
  const asked = manage || reviewer || author;
  const allowed = !asked || wanted.some(Boolean);

  if (allowed) return children;

  // Keep the app chrome. The person is signed in and has *some* role here —
  // denying one screen shouldn't strand them on a page with a single button.
  return (
    <div className="min-h-screen bg-background">
      <UniversalNavbar />
      <div className="page-shell py-16">
      <div className="max-w-md mx-auto text-center">
        <div className="w-11 h-11 rounded-lg bg-muted flex items-center justify-center mx-auto mb-3">
          <Icon name="Lock" size={20} className="text-muted-foreground" />
        </div>
        <h1 className="text-base font-semibold text-foreground">
          {t('access.deniedTitle', 'You don’t have access to this')}
        </h1>
        <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
          {t(
            'access.deniedBody',
            'This screen is for the people running this conference. If you think you should have access, ask one of its chairs to add you.'
          )}
        </p>
        <div className="mt-5 flex items-center justify-center gap-2">
          <Button variant="default" size="sm" onClick={() => navigate('/user-dashboard')} iconName="ArrowLeft" iconPosition="left">
            {t('access.backToDashboard', 'Back to dashboard')}
          </Button>
        </div>
        </div>
      </div>
    </div>
  );
};

export default RequireConferenceRole;
